import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import usersService from "../../services/userService";
import { AppDispatch } from "store/Store";
import { clearAuthState } from "./cognitoAuthSlice";
import { clearCookies, getAccessToken } from "services/cookieService";
import toast from "react-hot-toast";
import { IUser } from "Interfaces/interface.user";
import { setUser } from "./authSlice";
import { extractErrorMessage } from "utils/ErrorHandlers";
import { clearCart } from "./cartSlice";
interface IAuthState {
  user: IUser | null;
  loading: boolean;
  error: string | null;
  userId: string | null;
  email: string;
  phoneNumber: string;
  isUserLoaded: boolean;
}

const initialState: IAuthState = {
  user: null,
  loading: false,
  error: null,
  userId: null,
  email: "",
  phoneNumber: "",
  isUserLoaded: false,
};

export const fetchUserById = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("auth/fetchUserById", async (_, { rejectWithValue, dispatch }) => {
  try {
    const token = getAccessToken();
    if (!token) {
      return rejectWithValue("No token available");
    }

    const userData = await usersService.getUserById();

    if (!userData?.id) {
      throw new Error("User not found or invalid response");
    }

    const user: IUser = {
      id: userData.id,
      emailAddress: userData.emailAddress,
      phoneNumber: userData.phoneNumber?.replace(/^\+91/, ""),
      name: userData.name,
      company: userData.company,
      emailVerified: userData.emailAddressVerified,
      phoneNumberVerified: userData.phoneNumberVerified,
    };
    dispatch(setUser(user));
    return user;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message);
    return rejectWithValue(message);
  }
});

export const deleteUserAccount = createAsyncThunk<
  void,
  undefined,
  { rejectValue: string; dispatch: AppDispatch }
>("auth/deleteUserAccount", async (_, { rejectWithValue, dispatch }) => {
  try {
    const response = await usersService.deleteUser();
    toast.success("Your account is deleted successfully!");
    if (response) {
      clearCookies();
      dispatch(clearAuthState());
      dispatch(clearCart());
    }
  } catch (error: any) {
    console.log(error);
    toast.error(extractErrorMessage(error));
    return rejectWithValue(error.message || "Failed to delete user");
  }
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUser: (state) => {
      state.user = null;
      state.userId = null;
      state.email = "";
      state.phoneNumber = "";
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        const user = action.payload;
        state.loading = false;
        state.user = user;
        state.userId = user?.id || null;
        state.email = user?.email || "";
        state.phoneNumber = user?.phoneNumber || "";
        state.isUserLoaded = true;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.user = null;
        state.userId = null;
        state.email = "";
        state.phoneNumber = "";
      })

      .addCase(deleteUserAccount.fulfilled, (state, action) => {
        state.user = null;
        state.userId = null;
        state.email = "";
        state.phoneNumber = "";
        state.loading = false;
        state.error = null;
        state.isUserLoaded = false;
      })
      .addCase(deleteUserAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const selectUser = (state: any) => state.user.user;
export const selectUserId = (state: any) => state.user.userId;
export const selectUserEmail = (state: any) => state.user.email;
export const selectUserPhone = (state: any) => state.user.phoneNumber;
export const selectUserLoading = (state: any) => state.user.loading;
export const selectUserError = (state: any) => state.user.error;
export const selectUserLoaded = (state: any) => state.user.isUserLoaded;
export const { clearUser } = userSlice.actions;
export default userSlice.reducer;
